import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AppMenuItem } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { SIDEBAR_MAX_VISIBLE_ITEMS } from "../../../../consts";
import { useHomeSidebar } from "../../../../hooks/useHomeSidebar";
import { DraggableItem } from "./components/DraggableItem/DraggableItem";
import { SidebarToolItem } from "./components/SidebarToolItem";

export const MenuSetter = ({
  pinnedTools,
  onPinClick,
  onReorder,
}: {
  pinnedTools: AppMenuItem[];
  onPinClick: (e: React.SyntheticEvent, itemKey: AppMenuItem) => void;
  onReorder: (sourceIndex: number, destinationIndex: number) => void;
}) => {
  const { t } = useTranslations();
  const { allItems } = useHomeSidebar();

  const [activeDraggableId, setActiveDraggableId] =
    useState<AppMenuItem | null>(null);

  const pinnedToolsList = pinnedTools
    .map((itemKey) => allItems.find((tool) => tool.itemKey === itemKey))
    .filter(Boolean);

  const unPinnedToolsList = allItems.filter(
    (tool) => tool.itemKey && !pinnedTools.includes(tool.itemKey)
  );

  const activeDraggableTool = useMemo(
    () =>
      pinnedToolsList.find((tool) => tool?.itemKey === activeDraggableId) ??
      null,
    [activeDraggableId, pinnedToolsList]
  );

  const hasOverflowItems = allItems.length > SIDEBAR_MAX_VISIBLE_ITEMS;

  const sensors = useSensors(useSensor(MouseSensor));

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeItem = pinnedTools.find((item) => item === event.active.id);

      if (activeItem) {
        setActiveDraggableId(activeItem);
      }
    },
    [pinnedTools]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraggableId(null);

      const { active, over } = event;
      const activeIndex = active.data.current?.index;
      const overIndex = over?.data.current?.index;

      if (
        typeof activeIndex !== "number" ||
        typeof overIndex !== "number" ||
        activeIndex === overIndex
      ) {
        return;
      }

      onReorder(activeIndex, overIndex);
    },
    [onReorder]
  );

  return (
    <div className="flex h-full w-full flex-col items-start justify-start font-secondary">
      <div className="flex flex-col gap-2">
        <div className="pt-2 text-2xl font-medium text-dark">
          {t({
            id: "personalize-menu-dialog.menu-setter.title",
            defaultMessage: "Personalise menu items",
          })}
        </div>
        <div className="text-sm text-dark-700">
          {hasOverflowItems
            ? t(
                {
                  id: "personalize-menu-dialog.menu-setter.description-count",
                  defaultMessage:
                    "Select up to {count} items that you would like to pin in the side menu 📌",
                },
                { count: SIDEBAR_MAX_VISIBLE_ITEMS }
              )
            : t({
                id: "personalize-menu-dialog.menu-setter.description",
                defaultMessage:
                  "Select items that you would like to pin in the side menu 📌",
              })}
        </div>
      </div>
      <div className="jugl__custom-scrollbar w-full overflow-y-auto">
        <div className="flex w-full justify-between px-2 pt-10 pb-2 text-xs text-dark-700">
          {t({
            id: "personalize-menu-dialog.menu-setter.pinned-items-section-title",
            defaultMessage: "Pinned to side menu",
          })}
          {hasOverflowItems && (
            <div>
              {pinnedTools.length}/{SIDEBAR_MAX_VISIBLE_ITEMS}
            </div>
          )}
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pinnedTools}
            strategy={verticalListSortingStrategy}
          >
            <div
              className="flex w-full flex-col gap-1 pt-1 pr-1"
              style={{
                minHeight: `${pinnedTools.length * 45}px`,
              }}
            >
              {pinnedToolsList.map(
                (tool, index) =>
                  tool?.itemKey && (
                    <DraggableItem
                      key={tool.itemKey}
                      id={tool.itemKey}
                      index={index}
                    >
                      <SidebarToolItem
                        title={tool.title}
                        icon={tool.dialogIcon}
                        isPinned
                        onPinClick={(e) => onPinClick(e, tool.itemKey)}
                        isDraggable
                      />
                    </DraggableItem>
                  )
              )}
            </div>
          </SortableContext>
          {createPortal(
            <DragOverlay style={{ zIndex: 2001 }}>
              {activeDraggableTool?.itemKey && (
                <SidebarToolItem
                  title={activeDraggableTool.title}
                  icon={activeDraggableTool.dialogIcon}
                  isPinned
                  onPinClick={() => {}}
                  isDraggable
                />
              )}
            </DragOverlay>,
            document.body
          )}
        </DndContext>

        {!!unPinnedToolsList.length && (
          <>
            <div className="flex w-full justify-between px-2 pt-10 pb-2 text-xs text-dark-700">
              {t({
                id: "personalize-menu-dialog.menu-setter.unpinned-items-section-title",
                defaultMessage: "Shown under “More” menu",
              })}
            </div>
            <div className="flex w-full flex-col gap-1 pr-1">
              {unPinnedToolsList.map((tool) => (
                <SidebarToolItem
                  key={tool.title}
                  title={tool.title}
                  icon={tool.dialogIcon}
                  isPinned={false}
                  isAtLimit={pinnedTools.length >= SIDEBAR_MAX_VISIBLE_ITEMS}
                  onPinClick={(e) =>
                    tool.itemKey && onPinClick(e, tool.itemKey)
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
